import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../config/api';

const GestionAdministradores = () => {
  const [administradores, setAdministradores] = useState([]);
  const [formulario, setFormulario] = useState({ usuario: '', contrasena: '', rol: 'admin' });
  const [editandoId, setEditandoId] = useState(null);

  // ✅ Buscador
  const [busqueda, setBusqueda] = useState('');

  // ✅ Modal ver (solo lectura)
  const [modalAbierto, setModalAbierto] = useState(false);
  const [adminVista, setAdminVista] = useState(null);

  const cargarAdministradores = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/administradores`);
      if (!res.ok) throw new Error('No se pudo cargar administradores');
      const data = await res.json();
      setAdministradores(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error al cargar administradores:', err);
      setAdministradores([]);
    }
  };

  useEffect(() => {
    cargarAdministradores();
  }, []);

  // ✅ Cerrar modal con ESC
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setModalAbierto(false);
        setAdminVista(null);
      }
    };
    if (modalAbierto) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modalAbierto]);

  const resetFormulario = () => {
    setFormulario({ usuario: '', contrasena: '', rol: 'admin' });
  };

  const manejarCambio = (e) => {
    const { name, value } = e.target;
    setFormulario({ ...formulario, [name]: value });
  };

  const enviarFormulario = async (e) => {
    e.preventDefault();

    const url =
      editandoId === null
        ? `${API_BASE}/api/administradores`
        : `${API_BASE}/api/administradores/${editandoId}`;

    const metodo = editandoId === null ? 'POST' : 'PUT';

    try {
      const res = await fetch(url, {
        method: metodo,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formulario),
      });

      if (!res.ok) {
        let msg = 'Error al guardar administrador';
        try {
          const data = await res.json();
          if (data?.mensaje) msg = data.mensaje;
        } catch {}
        alert(msg);
        return;
      }

      try { await res.json(); } catch {}

      await cargarAdministradores();
      resetFormulario();
      setEditandoId(null);
    } catch (err) {
      console.error('Error al guardar administrador:', err);
      alert('Error de conexión al guardar administrador');
    }
  };

  const editarAdministrador = (admin) => {
    setFormulario({
      usuario: admin.usuario || '',
      contrasena: admin.contrasena || '',
      rol: admin.rol || 'admin',
    });
    setEditandoId(admin.id);
  };

  const cancelarEdicion = () => {
    resetFormulario();
    setEditandoId(null);
  };

  const eliminarAdministrador = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este administrador?')) return;

    try {
      const res = await fetch(`${API_BASE}/api/administradores/${id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        let msg = 'Error al eliminar administrador';
        try {
          const data = await res.json();
          if (data?.mensaje) msg = data.mensaje;
        } catch {}
        alert(msg);
        return;
      }

      try { await res.json(); } catch {}
      await cargarAdministradores();
    } catch (err) {
      console.error('Error al eliminar administrador:', err);
      alert('Error de conexión al eliminar administrador');
    }
  };

  const verAdmin = (admin) => {
    setAdminVista(admin);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setAdminVista(null);
  };

  const renderFila = (label, value) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 border-b">
      <div className="text-sm font-semibold text-gray-700">{label}</div>
      <div className="md:col-span-2 text-sm text-gray-800 break-words">
        {value && String(value).trim() !== '' ? value : <span className="text-gray-400">-</span>}
      </div>
    </div>
  );

  const adminsFiltrados = useMemo(() => {
    const q = (busqueda || '').trim().toLowerCase();
    if (!q) return administradores;

    return (administradores || []).filter((a) => {
      const usuario = (a.usuario || '').toLowerCase();
      const rol = (a.rol || '').toLowerCase();
      const id = String(a.id || '').toLowerCase();
      return usuario.includes(q) || rol.includes(q) || id.includes(q);
    });
  }, [administradores, busqueda]);

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-blue-700 mb-4">
        Gestión de Administradores y Técnicos
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ============================= */}
        {/* SECCIÓN 1: REGISTRAR / EDITAR */}
        {/* ============================= */}
        <div className="bg-white shadow-md rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {editandoId === null ? 'Registrar nuevo usuario' : 'Editar usuario'}
            </h3>

            {editandoId !== null && (
              <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-800">
                Modo edición
              </span>
            )}
          </div>

          {editandoId !== null && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded flex items-center justify-between">
              <div className="text-sm">Estás editando un usuario.</div>
              <button
                type="button"
                onClick={cancelarEdicion}
                className="bg-gray-700 hover:bg-gray-800 text-white px-3 py-2 rounded"
              >
                Cancelar edición
              </button>
            </div>
          )}

          <form onSubmit={enviarFormulario} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                name="usuario"
                value={formulario.usuario}
                onChange={manejarCambio}
                placeholder="Usuario"
                required
                className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <input
                type="password"
                name="contrasena"
                value={formulario.contrasena}
                onChange={manejarCambio}
                placeholder="Contraseña"
                required
                className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <select
                name="rol"
                value={formulario.rol}
                onChange={manejarCambio}
                className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="admin">Administrador</option>
                <option value="tecnico">Técnico</option>
              </select>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
              >
                {editandoId === null ? 'Registrar' : 'Guardar cambios'}
              </button>

              {editandoId === null && (
                <button
                  type="button"
                  onClick={resetFormulario}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition"
                >
                  Limpiar
                </button>
              )}
            </div>
          </form>
        </div>

        {/* ============================= */}
        {/* SECCIÓN 2: LISTADO + BUSCAR */}
        {/* ============================= */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-semibold text-gray-800">Registrados</h3>
              <span className="text-sm text-gray-500">{adminsFiltrados.length}</span>
            </div>

            <div className="relative w-full max-w-xs">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔎</span>
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por usuario/rol..."
                className="w-full border rounded pl-9 pr-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden border">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium">ID</th>
                  <th className="px-6 py-3 text-left text-sm font-medium">Usuario</th>
                  <th className="px-6 py-3 text-left text-sm font-medium">Rol</th>
                  <th className="px-6 py-3 text-left text-sm font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {adminsFiltrados.map((admin) => (
                  <tr key={admin.id} className="border-b hover:bg-gray-50">
                    <td className="px-6 py-4">{admin.id}</td>
                    <td className="px-6 py-4">{admin.usuario}</td>
                    <td className="px-6 py-4 capitalize">{admin.rol}</td>
                    <td className="px-6 py-4 flex gap-2">
                      <button
                        onClick={() => verAdmin(admin)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Ver"
                      >
                        👁️
                      </button>
                      <button
                        onClick={() => editarAdministrador(admin)}
                        className="text-yellow-600 hover:text-yellow-800"
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => eliminarAdministrador(admin.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Eliminar"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}

                {adminsFiltrados.length === 0 && (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-gray-500">
                      No hay registros
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ============================= */}
      {/* MODAL VER ADMIN (SOLO LECTURA) */}
      {/* ============================= */}
      {modalAbierto && adminVista && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4"
          onClick={cerrarModal}
        >
          <div
            className="w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <div>
                <h3 className="text-lg font-bold">Datos del usuario</h3>
                <p className="text-sm opacity-90">
                  <span className="font-semibold">{adminVista.usuario}</span>
                </p>
              </div>
              <button
                onClick={cerrarModal}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded"
                title="Cerrar"
              >
                ✕
              </button>
            </div>

            <div className="p-5">
              <div className="space-y-1">
                {renderFila('ID', adminVista.id)}
                {renderFila('Usuario', adminVista.usuario)}
                {renderFila('Rol', adminVista.rol)}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={cerrarModal}
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                >
                  Cerrar
                </button>
              </div>

              <p className="mt-2 text-xs text-gray-500">
                Tip: también podés cerrar con <span className="font-semibold">ESC</span> o clic fuera.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionAdministradores;